import { z } from "zod";

import type { FetchApi } from "@common/hooks/useFetchApi";
import {
  AiNoticeAcknowledgementSchema,
  AiNoticeSchema,
  type AiNotice,
  type AiNoticeAcknowledgement,
} from "@common/schemas/ai-notice";

const OrgMetadataWithAiNoticeSchema = z.object({ ai_notice: AiNoticeSchema });

const AiNoticeAcknowledgementResponseSchema = z.object({
  acknowledgement: AiNoticeAcknowledgementSchema.nullable(),
});

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export const AI_NOTICE_ACKNOWLEDGEMENT_PATH = "/api/ai-notice-acknowledgement";

export function parseAiNotice(orgMetadata: unknown): AiNotice | null {
  const parsed = OrgMetadataWithAiNoticeSchema.safeParse(orgMetadata);
  if (!parsed.success || !parsed.data.ai_notice.enabled) return null;
  return parsed.data.ai_notice;
}

export function isAcknowledgementDue(
  notice: AiNotice,
  acknowledgement: AiNoticeAcknowledgement | null,
  now: Date,
): boolean {
  if (!acknowledgement) return true;
  if (acknowledgement.version !== notice.version) return true;
  if (notice.reprompt_days === undefined) return false;
  const elapsedMs = now.getTime() - new Date(acknowledgement.acknowledgedAt).getTime();
  return elapsedMs > notice.reprompt_days * MILLISECONDS_PER_DAY;
}

export async function fetchAiNoticeAcknowledgement(
  fetchWeb: FetchApi,
): Promise<AiNoticeAcknowledgement | null> {
  const response = await fetchWeb(AI_NOTICE_ACKNOWLEDGEMENT_PATH);
  return AiNoticeAcknowledgementResponseSchema.parse(response).acknowledgement;
}

export async function saveAiNoticeAcknowledgement(fetchWeb: FetchApi, version: string): Promise<void> {
  await fetchWeb(AI_NOTICE_ACKNOWLEDGEMENT_PATH, {
    method: "PUT",
    body: JSON.stringify({ version }),
  });
}

export const AI_NOTICE_ACKNOWLEDGED_EVENT = "ai_notice_acknowledged";

export function aiNoticeAcknowledgedEventProps(input: {
  orgId: string;
  userId: string;
  noticeVersion: string;
  surface: "web" | "word-addin";
}): Record<string, string> {
  return {
    org_id: input.orgId,
    user_id: input.userId,
    notice_version: input.noticeVersion,
    surface: input.surface,
  };
}
